import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { CalendarRange, CheckCheck, Plus, Ship } from 'lucide-react';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  FormField,
  PageHeader,
  SearchPanel,
  Select,
  StatusBadge,
  TextField,
  type Column,
} from '@/components';
import { useCurrentUser } from '@/auth/AuthProvider';
import { READ_ONLY_ROLES } from '@/auth/types';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { formatDate } from '@/lib/format';
import { BusinessOperatorSelect } from '@/features/masters/components/BusinessOperatorSelect';
import { useVessels, type VesselListFilter, type VesselListItem } from './api';
import './vessels.css';

const APPROVED_OPTIONS = [
  { value: 'true', label: '承認済み' },
  { value: 'false', label: '未承認' },
];

/** 検索条件は URL クエリに保持する（詳細から戻っても条件が残る） */
function useFilterFromUrl(): [VesselListFilter, (f: VesselListFilter) => void] {
  const [params, setParams] = useSearchParams();
  const filter = useMemo<VesselListFilter>(
    () => ({
      businessOperatorId: params.get('businessOperatorId') ?? '',
      name: params.get('name') ?? '',
      registrationNo: params.get('registrationNo') ?? '',
      displayOrder: params.get('displayOrder') ?? '',
      approved: (params.get('approved') ?? '') as VesselListFilter['approved'],
    }),
    [params],
  );
  const setFilter = (f: VesselListFilter) => {
    const next = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && next.set(k, String(v)));
    setParams(next, { replace: true });
  };
  return [filter, setFilter];
}

/** S-10 参加船名簿一覧 */
export function VesselListPage() {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const { year, yearPath } = useFiscalYear();
  const [filter, setFilter] = useFilterFromUrl();
  const { data, isPending, isFetching, error, refetch } = useVessels(year, filter);
  const { register, handleSubmit, reset, setValue, watch } = useForm<VesselListFilter>({ values: filter });
  const readOnly = READ_ONLY_ROLES.includes(user.organizationType);
  const isAdmin = user.organizationType === '基金';

  const columns: Column<VesselListItem>[] = [
    { key: 'displayOrder', header: '名簿No', numeric: true, sortable: true, width: 84 },
    { key: 'vesselNo', header: '参加船ID', numeric: true, sortable: true, width: 92 },
    { key: 'businessOperatorName', header: '事業実施者', sortable: true, width: 170 },
    {
      key: 'name',
      header: '船名',
      sortable: true,
      render: (v) => (
        <span>
          <strong>{v.name}</strong>
          <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)' }}>{v.nameKana}</span>
        </span>
      ),
    },
    { key: 'registrationNo', header: '漁船登録番号', sortable: true, width: 130, render: (v) => <span className="bs-num">{v.registrationNo}</span> },
    { key: 'tonnage', header: 'トン数', numeric: true, sortable: true, width: 80, accessor: (v) => v.tonnage ?? 0, render: (v) => (v.tonnage ?? 0).toFixed(2) },
    { key: 'captainName', header: '船長', width: 120, render: (v) => v.captainName ?? '—' },
    { key: 'totalBoardingCount', header: '全乗船人員', numeric: true, width: 96, accessor: (v) => v.totalBoardingCount ?? 0 },
    {
      key: 'approvalFrom',
      header: '承認状態・期間',
      width: 190,
      sortable: true,
      render: (v) =>
        v.isApproved ? (
          <span>
            <StatusBadge tone="success" dot>承認済み</StatusBadge>
            <span className="bs-num" style={{ display: 'block', fontSize: 12, marginTop: 2, whiteSpace: 'nowrap' }}>{formatDate(v.approvalFrom)}〜{formatDate(v.approvalTo)}</span>
          </span>
        ) : (
          <StatusBadge tone="warning" dot>未承認</StatusBadge>
        ),
    },
  ];

  const operatorId = watch('businessOperatorId');
  const summary = [filter.businessOperatorId && `事業実施者ID: ${filter.businessOperatorId}`, filter.name && `船名: ${filter.name}`, filter.registrationNo && `登録番号: ${filter.registrationNo}`, filter.displayOrder && `名簿No: ${filter.displayOrder}`, filter.approved && (filter.approved === 'true' ? '承認済み' : '未承認')]
    .filter(Boolean)
    .join(' ／ ');

  return (
    <>
      <PageHeader
        eyebrow="船舶・計画"
        title="参加船名簿"
        description="事業に参加する船の名簿です。承認済みの船だけが、事前連絡・定時連絡・速報などの入力で選択できます。"
        actions={
          <>
            {isAdmin && (
              <Button icon={<CheckCheck size={16} />} onClick={() => navigate(yearPath('vessels/bulk-approval'))}>
                一括承認
              </Button>
            )}
            {!readOnly && (
              <Button icon={<CalendarRange size={16} />} onClick={() => navigate(yearPath('vessels/plan-days'))}>
                計画日数登録
              </Button>
            )}
            {!readOnly && (
              <Button variant="primary" icon={<Plus size={16} />} onClick={() => navigate(yearPath('vessels/new'))}>
                新規登録
              </Button>
            )}
          </>
        }
      />

      <SearchPanel
        onSubmit={handleSubmit((v) => setFilter(v))}
        onReset={() => {
          reset({ businessOperatorId: '', name: '', registrationNo: '', displayOrder: '', approved: '' });
          setFilter({});
        }}
        summary={summary || '条件なし'}
        searching={isFetching}
        columns={5}
      >
        <FormField label="事業実施者">
          <BusinessOperatorSelect
            fiscalYear={year}
            value={operatorId ? Number(operatorId) : null}
            onChange={(v) => setValue('businessOperatorId', v == null ? '' : String(v))}
          />
        </FormField>
        <FormField label="船名" hint="カナも検索できます">
          <TextField {...register('name')} />
        </FormField>
        <FormField label="漁船登録番号">
          <TextField {...register('registrationNo')} />
        </FormField>
        <FormField label="名簿No">
          <TextField inputMode="numeric" width="sm" {...register('displayOrder')} />
        </FormField>
        <FormField label="承認状態">
          <Select placeholder="すべて" options={APPROVED_OPTIONS} width="sm" {...register('approved')} />
        </FormField>
      </SearchPanel>

      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        // 一覧の項目名は中央寄せ（R08.10.08 検証指摘「一覧の項目名は、真ん中寄せでそろえる」。vessels.css .bs-vessel-list）
        <div className="bs-vessel-list">
          <DataTable
            caption="参加船名簿一覧"
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(v) => v.id}
            loading={isPending}
            defaultSort={{ key: 'displayOrder', direction: 'asc' }}
            pagination={{ pageSize: 25 }}
            dense
            onRowClick={(v) => navigate(yearPath(`vessels/${v.id}`))}
            rowClassName={(v) => (v.isApproved ? undefined : 'is-muted')}
            empty={<EmptyState compact icon={<Ship />} title="該当する参加船はありません" description="検索条件を変更するか、新規登録してください。" />}
          />
        </div>
      )}
    </>
  );
}
